import { makeAutoObservable, runInAction } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import {
  CountryCapital,
  CountryDetails,
  PinnedCountriesList,
} from '../models/shipmentCountryInfo';
import { CountrySetting, CountrySettingEntry } from '../models/countriesModel';
import { handleError } from '../utils/generic/generic.utils';
import { store } from './store';
import agent from '../api/agent';
import { ShipmentType } from '../models/apiResponse';

export default class ShipmentCountriesStore {
  private directionsListPromise: Promise<void> | undefined;

  private directionsListRequestKey: string | undefined;

  isDirectionsListApiLoading: boolean = false;

  version: string | undefined = undefined;

  senderCountries: CountryDetails[] = [];
  receiverCountries: CountryDetails[] = [];

  senderPinnedList: string[] = [];
  receiverPinnedList: PinnedCountriesList[] = [];

  defaultSettings: CountrySetting[] = [];
  settings: CountrySettingEntry[] = [];
  allowedShipmentTypes: ShipmentType[] = [];

  capitals: CountryCapital[] = [];

  constructor() {
    makeAutoObservable(this);

    makePersistable(this, {
      name: 'ShipmentCountriesLocalStore',
      properties: [
        'version',
        'senderCountries',
        'receiverCountries',
        'senderPinnedList',
        'receiverPinnedList',
        'defaultSettings',
        'settings',
        'capitals',
        'allowedShipmentTypes',
      ],
      storage: window.localStorage,
    });
    makePersistable(this, {
      name: 'ShipmentCountriesSessionStore',
      properties: [],
      storage: window.sessionStorage,
    });
  }

  getSettingsOrDefault = (countryCode: string | undefined) =>
    this.settings.find((x) => x.country === countryCode)?.settings ??
    this.defaultSettings ??
    [];

  getCapital = (countryCode: string | undefined) =>
    this.capitals.find((x) => x.country === countryCode);

  getAllowedShipmentTypes = (countryCode: string | undefined) =>
    this.allowedShipmentTypes.find((x) => x.country === countryCode)
      ?.shipment_types ??
    this.allowedShipmentTypes.find((x) => x.country === 'default')
      ?.shipment_types ??
    [];

  tryGetCountryLocalizedLabel = (countryCode: string) =>
    this.senderCountries.find((x) => x.code === countryCode)?.localized_name ??
    this.receiverCountries.find((x) => x.code === countryCode)
      ?.localized_name ??
    countryCode;

  private isGuest = () =>
    store.commonStore.isGuestParcelCreation || !store.userStore.user;

  private getDirectionsListRequestKey = () =>
    [
      this.isGuest() ? 'guest' : 'authenticated',
      store.localizationsStore.languageCode,
      store.localizationsStore.selectedCountry?.country_code,
    ].join(':');

  checkVersion = async () => {
    if (!this.version || this.allowedShipmentTypes.length === 0) return false;

    const checkVersion = this.isGuest()
      ? agent.Guest.checkDirectionsListVersion
      : agent.Shipment.checkDirectionsListVersion;

    try {
      const versionRes = await checkVersion(this.version);
      return versionRes?.checking_status;
    } catch (error) {
      handleError(error);
    }
  };

  loadDirectionsList = async (): Promise<void> => {
    const requestKey = this.getDirectionsListRequestKey();

    if (this.directionsListPromise) {
      if (this.directionsListRequestKey === requestKey) {
        return this.directionsListPromise;
      }

      await this.directionsListPromise;
      return this.loadDirectionsList();
    }

    const request = this.loadDirectionsListRequest();
    this.directionsListPromise = request;
    this.directionsListRequestKey = requestKey;

    try {
      await request;
    } finally {
      if (this.directionsListPromise === request) {
        this.directionsListPromise = undefined;
        this.directionsListRequestKey = undefined;
      }
    }
  };

  private loadDirectionsListRequest = async () => {
    this.isDirectionsListApiLoading = true;

    try {
      if (await this.checkVersion()) return;

      const loadDirectionsList = this.isGuest()
        ? agent.Guest.listDirections
        : agent.Shipment.listDirections;

      const directionsRes = await loadDirectionsList();

      if (!directionsRes)
        throw Error('Failed to load list of shipping directions.');

      this.version = directionsRes.version;

      this.senderCountries = directionsRes.sender_countries;
      this.receiverCountries = directionsRes.receiver_countries;

      this.senderPinnedList = directionsRes.sender_pinned_list;
      this.receiverPinnedList = directionsRes.receiver_pinned_list;

      this.defaultSettings = directionsRes.default_settings;
      this.settings = directionsRes.settings;
      this.allowedShipmentTypes = directionsRes.allowed_shipment_types;

      this.capitals = directionsRes.capitals;
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isDirectionsListApiLoading = false;
      });
    }
  };

  getCountrySetting = (countryCode: string | undefined, settingName: string) =>
    this.settings
      .find((x) => x.country === countryCode)
      ?.settings.find((y) => y.name === settingName);
}
